import React, { useContext, useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getImage } from "../Utils/image_helper";
import { ConfigContext } from "../context/GlobalContext";
import { GetAllData } from "../data";
import { LoginFunc } from "../data/Firebase/Authentication";
import LoaderView from "./LoaderView";
import { OtpModal } from "./OtpModal";
import { auth, RecaptchaVerifier } from "../Utils/firebase";
import { signInWithPhoneNumber } from "firebase/auth";
import { LoginWithPhone } from "../data/Firebase/Authentication/LoginWithPhone";
import Country from "../Utils/countriesJson.json";
import * as actionType from "../context/actions";
import { SendOTPAPI } from "../AuthenticationAPIs/SendOTPAPI";
import ErrorModal from "./ErrorModal";
import { VerifyOTPAPI } from "../AuthenticationAPIs/VerifyOTPAPI";
import { MessageSquare, FileSignature, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import CountUp from "react-countup";
import { FaUsers, FaBuilding, FaFileInvoice } from "react-icons/fa";
import { logEvent } from "../helper/shakebug";
// import WhatsappImage from "../../public/assets/images/Generatedwhatsapp.png";

const slides = [
  {
    id: 1,
    icon: MessageSquare,
    title: "Send via WhatsApp",
    description: "The ability to instantly share your invoices via WhatsApp, cutting down delays and helping you get paid faster with just a tap.",
    image: "/assets/images/Generatedwhatsapp.png",
    color: "bg-primary"
  },
  {
    id: 2,
    icon: FileSignature,
    title: "Digital Signatures",
    description: "The power to add your digital signature seamlessly on every document, giving your invoices and quotes a professional, trustworthy edge that clients appreciate.",
    image: "/assets/images/digitalSignature.webp",
    color: "bg-primary"
  },
  {
    id: 3,
    icon: FileText,
    title: "Professional Quotations",
    description: "Got a client interested? Send a professional quotation instantly. Once approved, the invoice is ready to share directly on WhatsApp, complete with your digital signature.",
    image: "/assets/images/quotation.webp",
    color: "bg-primary"
  }
];

const IMAGES = {
  whatsapp: "../../public/assets/images/Generatedwhatsapp.png",
  signature: "/assets/images/getimage.png",
  quotation: "/assets/images/quotation.webp",
};


const SignInLayer = () => {
  const { state, dispatch } = useContext(ConfigContext);
  const location = useLocation();
  const customMessage = location.state?.message;

  const [isLoading, setIsLoading] = useState(false);
  const [mobile, setMobile] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [filteredCountries, setFilteredCountries] = useState(Country);
  const [searchBuffer, setSearchBuffer] = useState("");
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fullPhoneNumber = `${countryCode}${mobile}`;
  const target = "24000"; // or "24K" if you want to parse it
  const duration = 2000; // in ms

  const texts = [
    {
      title: "Create & Send Quotations Easily",
      description: "Generate professional quotations in just a few clicks."
    },
    {
      title: "Get Your Signature Digitally",
      description: "Sign documents online securely with our digital signature feature."
    },
    {
      title: "Connect with WhatsApp Instantly",
      description: "Send invoices and updates directly through WhatsApp."
    }
  ];


  const handleMobileSubmit = async () => {
    setIsLoading(true);
    try {

      const result = await SendOTPAPI(fullPhoneNumber.trim());

      if (result.status) {
        setShowOtpModal(true);
      }
      else {
        setErrorMessage(result.error || "Failed to send OTP.");
        setErrorModalVisible(true);
      }


    } catch (error) {
      console.error("SMS not sent", error);

      let message = "Something went wrong. Please try again.";

      setErrorMessage(message || "Failed to send OTP.");
      setErrorModalVisible(true);

    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    if (otp) {
      try {
        setIsLoading(true)
        const result = await VerifyOTPAPI(fullPhoneNumber, otp);

        if (result?.status) {
          setShowOtpModal(false);
          logEvent("User Login", "phone");
          LoginWithPhone(fullPhoneNumber, result?.token, result?.uid, state, dispatch, setIsLoading);
        }
        else {
          setOtpError(result?.error || "Invalid OTP. Please try again.")
        }

      } catch (error) {
        setOtpError("Invalid OTP. Please try again.");
      }
    }
  };

  const OnResendOTP = () => {
    handleMobileSubmit();
  };

  useEffect(() => {
    setFilteredCountries(Country); // on mount or country list change
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const currentFeature = slides[currentIndex];

  const [marginTop, setMarginTop] = useState(
    window.innerHeight < 500 ? "50%" : "2rem"
  );

  useEffect(() => {
    const handleResize = () => {
      setMarginTop(window.innerHeight < 500 ? "50%" : "2rem");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section className="auth d-flex flex-wrap bg-light min-vh-100 overflow-x-hidden align-items-stretch">
      {/* Left Section */}
      <div className="position-relative overflow-hidden auth-left text-white d-none d-lg-flex col-lg-5 p-4 rounded">

        {/* Background */}
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{
            backgroundImage: `linear-gradient(rgba(24, 119, 242, 0.8), rgba(24, 119, 242, 0.8)), url('${currentFeature.image}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />


        {/* Content */}
        <div className="position-relative z-2 h-100 d-flex align-items-center px-4 px-lg-5"
        >
          <div
            className="p-5 rounded"
            style={{ maxWidth: "700px", }}

          >
            <h3 className="display-5 fw-bold mb-3 text-white">{currentFeature.title}</h3>
            <p className="lead mb-4 text-white">{currentFeature.description}</p>

            {/* Stats */}
            <div className="row text-center text-white mt-5">
              <div className="col">
                <FaUsers size={40} className="mb-2 text-white" />
                <h3 className="text-white">
                  <CountUp end={38} duration={2} />K+
                </h3>
                <p>Registered Users</p>
              </div>
              <div className="col">
                <FaBuilding size={40} className="mb-2 text-white" />
                <h3 className="text-white">
                  <CountUp end={84} duration={2} />K+
                </h3>
                <p>Registered Businesses</p>
              </div>
              <div className="col">
                <FaFileInvoice size={40} className="mb-2 text-white" />
                <h3 className="text-white">
                  <CountUp end={150} duration={2} />K+
                </h3>
                <p>Invoices</p>
              </div>
            </div>
          </div>
        </div>

        <div
          className="position-absolute bottom-0 start-0 w-100 bg-white opacity-25"
          style={{ height: "4px" }}
        >
          <div
            className="bg-white"
            style={{
              width: `${((currentIndex + 1) / slides.length) * 100}%`,
              height: "4px",
              transition: "width 0.5s ease",
            }}
          />
        </div>
      </div>


      {/* Right Section */}
      <div className="auth-right col-lg-7 col-12 d-flex flex-column justify-content-center align-items-center p-5 auth-right-scroll">
        <div className="max-w-464-px mx-auto w-100 text-center" style={{ maxWidth: "464px", marginTop }}>
          <Link to="/dashboard" className="mb-4 d-block">
            <img src={getImage("logo")} alt="logo" className="img-fluid" style={{ width: "70%" }} />
          </Link>

          <h4 className="mb-3 text-dark fw-bold fs-4 mt-5">
            {customMessage ? "Action Required" : "Welcome back!"}
          </h4>
          <p className="mb-4 text-secondary">
            {customMessage ? customMessage : "Sign in using your preferred method below"}
          </p>

          <div className="text-start mt-5 w-100" style={{ maxWidth: "464px" }}>
            <div className="row g-0 align-items-stretch flex-wrap flex-md-nowrap w-100" style={{ maxWidth: "454px" }}>
              <div className="col-2 col-md-2">
                <div className="dropdown h-100">
                  <button
                    className="btn border bg-white w-100 text-center d-flex justify-content-center align-items-center h-100"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{
                      minWidth: "110px",
                      borderTopLeftRadius: "0.5rem",
                      borderBottomLeftRadius: "0.5rem",
                      borderTopRightRadius: "0",
                      borderBottomRightRadius: "0"
                    }}
                  >
                    {(() => {
                      const selected = Country.find(c => c.dialCode == countryCode);
                      return (
                        <>
                          <img
                            src={selected?.image}
                            alt="flag"
                            style={{ width: "25px", height: "25px", marginRight: "6px" }}
                          />
                          {countryCode}
                        </>
                      );
                    })()}
                  </button>
                  <ul
                    className="dropdown-menu w-100 shadow-sm border-0 rounded-0 bg-white text-center hide-scrollbar p-1"
                    style={{ minWidth: "110px", padding: 0 }}
                  >
                    <li className="px-2 py-2 sticky-top z-1 bg-white">
                      <input
                        type="text"
                        className="form-control bg-white signin-input countrybackground "
                        placeholder="Search"
                        value={searchBuffer}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSearchBuffer(value);
                          const trimmed = value.trim().toLowerCase();
                          if (trimmed === "") {
                            setFilteredCountries(Country);
                          } else {
                            const filtered = Country.filter(
                              (country) =>
                                country.name.toLowerCase().includes(trimmed) ||
                                country.dialCode.includes(trimmed)
                            );
                            setFilteredCountries(filtered);
                          }
                        }}
                      />
                    </li>
                    <div
                      style={{ maxHeight: "250px", overflowY: "auto" }}
                      className="hide-scrollbar"
                    >
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country) => (
                          <li key={country.id}>
                            <a
                              className={`dropdown-item d-flex align-items-center gap-2 px-3 py-2 ${country.dialCode === countryCode ? "bg-light fw-semibold" : ""
                                }`}
                              onClick={(e) => {
                                e.preventDefault();
                                setCountryCode(country.dialCode);
                                setSearchBuffer("");
                                setFilteredCountries(Country);
                              }}
                            >
                              <img
                                src={country.image}
                                alt={country.name}
                                style={{ width: "25px", height: "18px", objectFit: "cover" }}
                              />
                              <span className="ms-1">{country.dialCode}</span>
                            </a>
                          </li>
                        ))
                      ) : (
                        <li className="text-muted text-center small py-2">No countries found</li>
                      )}
                    </div>
                  </ul>
                </div>
              </div>

              <div className="col-10 col-md-8">
                <input
                  type="tel"
                  className="form-control mobilefield h-100 border rounded-0 bg-white text-dark no-border-focus signin-input"
                  style={{ marginLeft: "6%" }}
                  placeholder="e.g. 9313405533"
                  value={mobile}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    if (numericValue.length <= 15) {
                      setMobile(numericValue);
                      setOtpError(null)
                    }
                  }}

                />
              </div>

              <div className="col-12 col-md-4 mt-3 mt-md-0 d-flex justify-content-center justify-content-md-start">
                <button
                  className="btn btn-primary px-4 rounded rounded-button"
                  id="sign-in-button"
                  type="button"
                  onClick={handleMobileSubmit}
                  disabled={!mobile}
                >
                  Send OTP
                </button>
              </div>

            </div>
          </div>



          <div className="d-flex align-items-center my-3">
            <hr className="flex-grow-1 border-secondary" />
            <span className="mx-3 text-muted fw-semibold">OR</span>
            <hr className="flex-grow-1 border-secondary" />
          </div>

          <div className="d-flex flex-column gap-3 mb-5 mt-5">
            {[
              { provider: "google", color: "#DB4437", icon: "logos:google-icon", text: "Sign In with Google" },
              { provider: "facebook", color: "#1877F2", icon: "ic:baseline-facebook", text: "Sign In with Facebook" },
              { provider: "apple", color: "#000", icon: "logos:apple", text: "Sign In with Apple" },
            ].map(({ provider, color, icon, text }) => (
              <button
                key={provider}
                type="button"
                className="btn d-flex align-items-center justify-content-center gap-2 py-3 rounded shadow-sm"
                onClick={() => {
                  setIsLoading(true);
                  LoginFunc(state, dispatch, provider, () => { }, setIsLoading);
                }}
                style={{ backgroundColor: "#fff", border: `1px solid ${color}`, color, fontWeight: "bold" }}
              >
                <Icon icon={icon} className="text-xl" />
                {text}
              </button>
            ))}
          </div>

          {/* <div className="mt-4 text-center">
            <Link
              onClick={() => {
                localStorage.setItem("isLocal", "true");
                GetAllData(state, dispatch);
              }}
              to="/dashboard"
            >
              <button className="btn btn-link text-dark fw-bold text-decoration-underline">
                Continue as Guest
              </button>
            </Link>
          </div> */}

          {/* ✅ Recaptcha container */}
          <div id="recaptcha-container"></div>
        </div>
      </div>
      {isLoading && <LoaderView isLogin={true} />}
      <OtpModal
        show={showOtpModal}
        onHide={() => {
          setIsLoading(false);
          setShowOtpModal(false);
        }}
        onSubmit={handleVerifyOtp}
        resendOtp={OnResendOTP}
        error={otpError}
      />

      <ErrorModal
        show={errorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        message={errorMessage}
      />

    </section>
  );
};

export default SignInLayer;
